import { useState, useMemo, useCallback, memo } from 'react'
import { Outlet, useLocation, useNavigate, Link } from 'react-router-dom'
import YogaLogo from './YogaLogo'
import { 
  LayoutDashboard, 
  Users, 
  School, 
  FileText, 
  BarChart3, 
  Settings, 
  Menu, 
  X, 
  LogOut,
  Bell,
  Search,
  HelpCircle,
  MessageSquare,
  HelpCircle as FAQIcon,
  Calendar,
  BookOpen,
  Percent,
  ClipboardList,
  UserPlus,
  ShieldCheck
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'

// Memoized navigation item component to prevent unnecessary re-renders
const NavigationItem = memo(({ item }: { item: any }) => (
  <Link
    to={item.href}
    className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-300 relative ${
      item.current
        ? 'bg-gradient-to-r from-amber-500/25 to-amber-600/25 backdrop-blur-sm text-white shadow-lg border-l-4 border-amber-400'
        : 'text-ivory-200 hover:bg-gradient-to-r hover:from-amber-500/10 hover:to-amber-600/10 hover:text-white hover:shadow-md hover:border-l-4 hover:border-amber-400/50'
    }`}
  >
    <div className={`w-8 h-8 rounded-lg flex items-center justify-center mr-3 transition-all duration-300 ${
      item.current 
        ? 'bg-amber-500/20 shadow-lg' 
        : 'bg-ivory-700/30 group-hover:bg-amber-500/20 group-hover:shadow-lg'
    }`}>
      <item.icon className={`w-4 h-4 ${
        item.current ? 'text-amber-200' : 'text-ivory-300 group-hover:text-amber-200'
      }`} />
    </div>
    <div className="flex-1">
      <span className="block font-semibold">{item.name}</span>
    </div>
    {item.current && (
      <div className="w-2 h-2 bg-amber-300 rounded-full flex-shrink-0"></div>
    )}
  </Link>
))

NavigationItem.displayName = 'NavigationItem'

// Memoized search component to prevent unnecessary re-renders
const SearchInput = memo(() => (
  <div className="hidden lg:block relative">
    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-ivory-400" />
    <input
      type="text"
      placeholder="Search..."
      className="pl-10 pr-4 py-2 w-48 text-sm border border-ivory-200 rounded-md focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-200 bg-ivory-100"
    />
  </div>
))

SearchInput.displayName = 'SearchInput'

// Memoized notification button
const NotificationButton = memo(() => (
  <button className="relative p-2 text-ivory-600 hover:text-ivory-700 hover:bg-ivory-100 rounded-md transition-all duration-200">
    <Bell className="w-4 h-4" />
    <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-semibold">
      5
    </span>
  </button>
))

NotificationButton.displayName = 'NotificationButton'

// Memoized help button
const HelpButton = memo(() => (
  <button className="p-2 text-ivory-600 hover:text-ivory-700 hover:bg-ivory-100 rounded-md transition-all duration-200">
    <HelpCircle className="w-4 h-4" />
  </button>
))

HelpButton.displayName = 'HelpButton'

const AdminLayout = memo(() => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout, hasPermission } = useAuth()



  // Static navigation items - no current path calculation here.
  // `permission` (when set) is the granular key required by SUPPORT_ADMIN.
  // SUPER_ADMIN bypasses every check via `hasPermission`.
  // `superAdminOnly: true` hides the entry from SUPPORT_ADMIN entirely.
  const navigationItems = useMemo(() => [
    {
      name: 'Dashboard',
      href: '/admin/dashboard',
      icon: LayoutDashboard,
    },
    {
      name: 'Users',
      href: '/admin/users',
      icon: Users,
      permission: 'users.view'
    },
    {
      name: 'Yoga Schools',
      href: '/admin/yoga-schools',
      icon: School,
      permission: 'schools.view'
    },
    {
      name: 'Onboard School',
      href: '/admin/onboard-school',
      icon: UserPlus,
      permission: 'schools.onboard'
    },
    {
      name: 'New Registrations',
      href: '/admin/registrations',
      icon: ClipboardList,
      permission: 'registrations.view'
    },
    {
      name: 'Courses',
      href: '/admin/courses',
      icon: BookOpen,
      permission: 'courses.view'
    },
    {
      name: 'Bookings',
      href: '/admin/bookings',
      icon: FileText,
      permission: 'bookings.view'
    },
    {
      name: 'Testimonials',
      href: '/admin/testimonials',
      icon: MessageSquare,
      permission: 'testimonials.manage'
    },
    {
      name: 'FAQs',
      href: '/admin/faqs',
      icon: FAQIcon,
      permission: 'faqs.manage'
    },
    {
      name: 'Consultations',
      href: '/admin/consultations',
      icon: Calendar,
      permission: 'consultations.manage'
    },
    {
      name: 'Analytics',
      href: '/admin/analytics',
      icon: BarChart3,
      permission: 'analytics.view'
    },
    {
      name: 'Admin Users',
      href: '/admin/admin-users',
      icon: ShieldCheck,
      superAdminOnly: true
    },
    {
      name: 'Settings',
      href: '/admin/settings',
      icon: Settings,
      superAdminOnly: true
    },
    {
      name: 'Commission Settings',
      href: '/admin/commission-settings',
      icon: Percent,
      superAdminOnly: true
    }
  ] as Array<{
    name: string
    href: string
    icon: any
    permission?: string
    superAdminOnly?: boolean
  }>, [])

  // Memoized filtered navigation items with current path calculation
  const filteredNavigationItems = useMemo(() => {
    if (!user) return []
    return navigationItems
      .filter(item => {
        if (item.superAdminOnly) return user.role === 'SUPER_ADMIN'
        if (item.permission) return hasPermission(item.permission)
        return true
      })
      .map(item => ({
        ...item,
        current: item.href === '/admin/dashboard'
          ? location.pathname === item.href
          : location.pathname.startsWith(item.href)
      }))
  }, [navigationItems, user, location.pathname, hasPermission])

  const handleLogout = useCallback(() => {
    logout()
    navigate('/login')
  }, [logout, navigate])

  const openSidebar = useCallback(() => {
    setSidebarOpen(true)
  }, [])

  const closeSidebar = useCallback(() => {
    setSidebarOpen(false)
  }, [])

  // Memoized breadcrumb text to prevent unnecessary re-renders
  const breadcrumbText = useMemo(() => {
    return location.pathname.split('/').pop()?.replace('-', ' ') || 'Dashboard'
  }, [location.pathname])

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="h-screen bg-ivory-50 flex overflow-hidden">
      {/* Sidebar */}
      <div 
        className={`${sidebarOpen ? 'block' : 'hidden'} lg:block w-56 bg-gradient-to-b from-ivory-800 to-ivory-900 shadow-2xl h-screen overflow-y-auto`}
        style={{
          scrollbarWidth: 'thin',
          scrollbarColor: '#d4c4a8 #8b7355',
          '--scrollbar-width': '2px',
          '--scrollbar-track': 'transparent',
          '--scrollbar-thumb': '#d4c4a8',
          '--scrollbar-thumb-hover': '#c4b5a0'
        } as React.CSSProperties}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="relative px-4 py-4 border-b border-ivory-600/20">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <div className="w-10 h-10 bg-gradient-to-br from-amber-400 via-amber-500 to-amber-600 rounded-xl flex items-center justify-center shadow-lg ring-2 ring-amber-500/20">
                  <YogaLogo size="sm" variant="light" showText={false} />
                </div>
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-ivory-800"></div>
              </div>
              <div className="flex-1">
                <h1 className="text-sm font-bold text-ivory-50">BookMyYogaTTC</h1>
                <div className="flex items-center mt-1">
                  <div className="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
                  <span className="text-xs text-ivory-400">Online</span>
                </div>
              </div>
            </div>
            <button
              onClick={closeSidebar}
              className="absolute top-3 right-3 lg:hidden p-2 rounded-lg hover:bg-ivory-700/50 transition-all duration-200"
            >
              <X className="w-4 h-4 text-ivory-300" />
            </button>
          </div>

          {/* User Info */}
          <div className="px-4 py-3 border-b border-ivory-600/20">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <div className="w-10 h-10 bg-gradient-to-br from-amber-400/20 to-amber-600/20 backdrop-blur-sm rounded-xl flex items-center justify-center ring-2 ring-amber-400/30 shadow-lg">
                  <div className="w-6 h-6 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-xs">
                      {user.firstName?.[0]}{user.lastName?.[0]}
                    </span>
                  </div>
                </div>
                <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-ivory-800"></div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-sm font-bold text-ivory-50 truncate">
                    {user.firstName} {user.lastName}
                  </h3>
                  <div className={`px-2 py-0.5 rounded text-xs font-semibold ${
                    user.role === 'SUPER_ADMIN' 
                      ? 'bg-red-500/20 text-red-300' 
                      : 'bg-amber-500/20 text-amber-300'
                  }`}>
                    {user.role === 'SUPER_ADMIN' ? 'Super' : 'Support'}
                  </div>
                </div>
                <p className="text-xs text-ivory-300 truncate">{user.email}</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            <div className="mb-4">
              <h4 className="text-xs font-semibold text-ivory-400 uppercase tracking-wider mb-3 px-2">
                Main Menu
              </h4>
              <div className="space-y-0.5">
                {filteredNavigationItems.slice(0, 5).map((item) => (
                  <NavigationItem key={item.name} item={item} />
                ))}
              </div>
            </div>
            
            <div className="mb-4">
              <h4 className="text-xs font-semibold text-ivory-400 uppercase tracking-wider mb-3 px-2">
                Management
              </h4>
              <div className="space-y-0.5">
                {filteredNavigationItems.slice(5).map((item) => (
                  <NavigationItem key={item.name} item={item} />
                ))}
              </div>
            </div>
          </nav>

          {/* Logout */}
          <div className="px-3 py-4 border-t border-ivory-600/20">
            <button
              onClick={handleLogout}
              className="w-full flex items-center px-3 py-2.5 text-sm font-medium text-ivory-200 hover:bg-gradient-to-r hover:from-red-500/20 hover:to-red-600/20 hover:text-red-100 rounded-lg transition-all duration-300 group"
            >
              <div className="w-8 h-8 bg-red-500/20 rounded-lg flex items-center justify-center mr-3 group-hover:bg-red-500/30">
                <LogOut className="w-4 h-4 text-red-300 group-hover:text-red-200" />
              </div>
              <span className="font-medium">Sign Out</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden h-screen">
        {/* Top header */}
        <header className="bg-ivory-50 shadow-sm border-b border-ivory-200">
          <div className="flex items-center justify-between h-14 px-4 sm:px-6 lg:px-8">
            <div className="flex items-center min-w-0">
              <button
                onClick={openSidebar}
                className="lg:hidden p-3 rounded-xl hover:bg-ivory-100 transition-colors"
              >
                <Menu className="w-6 h-6 text-ivory-600" />
              </button>
              
              {/* Breadcrumb */}
              <nav className="hidden md:flex items-center space-x-3 ml-6 min-w-0">
                <span className="text-sm text-ivory-500 font-medium">Admin</span>
                <span className="text-ivory-300">/</span>
                <span className="text-sm font-semibold text-ivory-700 capitalize truncate">
                  {breadcrumbText}
                </span>
              </nav>
            </div>

            <div className="flex items-center space-x-3">
              {/* Search */}
              <SearchInput />

              {/* Notifications */}
              <NotificationButton />

              {/* Help */}
              <HelpButton />
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 bg-ivory-50 overflow-y-auto main-scroll">
          <Outlet />
        </main>
      </div>
    </div>
  )
})

AdminLayout.displayName = 'AdminLayout'

export default AdminLayout

